import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { formatDateFromTimestamp } from '../../../utils/format';
import { Course, Prono, Reunion } from '../services/type';
import { useMediaQuery } from '@mui/material';

const LOCAL_STORAGE_KEY = 'pronosCache';

const getKey = (reunion: Reunion, course: Course) =>
  `${reunion.numOfficiel}-${course.numOrdre}-${formatDateFromTimestamp(course.heureDepart)}`;

type PronoCacheContextType = {
  toggleProno: (prono: Prono, reunion: Reunion, course: Course) => void;
  getPronosByCourse: (reunion: Reunion, course: Course) => number[]; // Retourne uniquement les numéros de prono
  hasProno: (prono: Prono, reunion: Reunion, course: Course) => boolean; // Vérifie si un prono est en cache
};

// Création du contexte avec valeurs par défaut
const PronoCacheContext = createContext<PronoCacheContextType>({
  toggleProno: () => { },
  getPronosByCourse: () => [],
  hasProno: () => false,
});

export const PronoCacheProvider = ({ children }: { children: ReactNode }) => {
  const [pronosCache, setPronosCache] = useState<Record<string, number[]>>({});
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));


  // Charger le cache depuis localStorage au montage
  useEffect(() => {
    const storedCache = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedCache) {
      setPronosCache(JSON.parse(storedCache));
    }
  }, []);

  // Met à jour le cache localStorage et le state
  const toggleProno = (prono: Prono, reunion: Reunion, course: Course) => {
    const key = getKey(reunion, course);

    setPronosCache((prevCache) => {
      const existing = prevCache[key] || [];
      const isAlreadySelected = existing.includes(prono.data.numero);

      const updated = isAlreadySelected
        ? existing.filter((num) => num !== prono.data.numero)
        : [...existing, prono.data.numero];

      const newCache = {
        ...prevCache,
        [key]: updated.length > 0 ? updated : [],
      };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newCache));
      setValidationMessage(
        isAlreadySelected
          ? `Cheval n°${prono.data.numero} supprimé de votre sélection.`
          : `Vous avez sélectionné le cheval n°${prono.data.numero}.`
      );

      return newCache;
    });
  };

  // Récupérer les numéros des pronos pour une course donnée avec useCallback
  const getPronosByCourse = (reunion: Reunion, course: Course): number[] => {
    const key = getKey(reunion, course);
    return pronosCache[key] || [];
  };

  // Vérifier si un prono est sélectionné pour une course donnée
  const hasProno = (prono: Prono, reunion: Reunion, course: Course): boolean => {
    const key = getKey(reunion, course);
    return pronosCache[key]?.includes(prono.data.numero) || false;
  };

  return (
    <PronoCacheContext.Provider
      value={{ toggleProno, getPronosByCourse, hasProno }}
    >
      {children}
      {validationMessage && (
        <Snackbar
          anchorOrigin={{ vertical: isMobile ? "bottom" : 'top', horizontal: 'right' }}
          open={!!validationMessage}
          autoHideDuration={1500}
          onClose={() => setValidationMessage(null)}
        >
          <Alert
            onClose={() => setValidationMessage(null)}
            severity="success"
            variant="filled"
          >
            <Typography>{validationMessage}</Typography>
          </Alert>
        </Snackbar>
      )}
    </PronoCacheContext.Provider>
  );
};

// Hook pour accéder au cache des pronos
export const usePronoCache = () => useContext(PronoCacheContext);
